import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Text,
  VStack,
  Button,
  Image,
  Stack,
  Avatar,
  HStack,
  Icon,
  Wrap,
  Tag,
  Card,
  CardBody,
  Link as ChakraLink,
  useDisclosure,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { HiLocationMarker, HiShieldCheck } from "react-icons/hi";
import { brandConfig, getColor } from "../brandConfig";
import { influencers, Influencer } from "../inflencers";
import { LoginWithSmsModal } from "../components/LoginWithSmsModal";
import { SecureMembershipButton } from "../components/SecureMembershipButton";

export const HomePage = () => {
  const [randomizedInfluencers, setRandomizedInfluencers] = useState<Influencer[]>([]);
  const { isOpen, onClose } = useDisclosure();

  // Function to randomize influencers
  const randomizeInfluencers = () => {
    const shuffled = [...influencers].sort(() => Math.random() - 0.5);
    setRandomizedInfluencers(shuffled);
  };

  // Call randomizeInfluencers when the component mounts
  useEffect(() => {
    randomizeInfluencers();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <>
      <NavbarWithCallToAction />
      
      {/* Hero Section */}
      <Box as="section" id="hero" bg={getColor("background.main")}>
        <Image
          alt="Multipolar Peace - Global Harmony"
          src="/assets/hero.webp"
          objectFit="cover"
          width="full"
          height="500px"
        />

        <Container maxW="container.xl" mt={12} mb={12}>
          <Stack spacing={{ base: "6", md: "8" }} align="center" textAlign="center">
            <Stack spacing="4">
              <Heading size={{ base: "lg", md: "2xl" }} fontWeight="semibold" color={getColor("primary")}>
                Welcome to {brandConfig.siteName}: {brandConfig.tagline}
              </Heading>
            </Stack>
            <Heading size={{ base: "md", md: "lg" }} color={getColor("secondary")}>
              Discover Unity in Diversity
            </Heading>
            <Text color={getColor("text.secondary")} fontSize={{ base: "lg", md: "xl" }} maxW="6xl" lineHeight="1.8">
              {brandConfig.description} Join us in this transformative journey as we explore diverse perspectives and bridge the gaps between different cultures and nations.
            </Text>
          </Stack>
        </Container>
      </Box>

      {/* Thought Leaders Section - NOW AT THE TOP */}
      <Box as="section" bg={getColor("background.light")}>
        <Container maxW="container.xl" py={{ base: "12", md: "16" }}>
          <VStack spacing={8} mb={12}>
            <Heading size={{ base: "lg", md: "xl" }} color={getColor("primary")} textAlign="center">
              Connect with Our Thought Leaders
            </Heading>
            <Text color={getColor("text.secondary")} textAlign="center" maxW="4xl" fontSize={{ base: "lg", md: "xl" }} lineHeight="1.7">
              Book sessions with leading voices in geopolitics, economics, and peace advocacy from around the world. These thought leaders are changing the global conversation.
            </Text>
          </VStack>
          
          <SimpleGrid columns={{ base: 1, md: 3, lg: 4 }} spacing={8}>
            {randomizedInfluencers.map((influencer) => (
              <Card key={influencer.name} bg={getColor("background.card")} boxShadow="lg" borderRadius="xl" overflow="hidden">
                <CardBody p={6}>
                  <VStack spacing={4} align="center">
                    <Image
                      src={influencer.profileImage}
                      alt={influencer.name}
                      boxSize="80px"
                      borderRadius="full"
                      objectFit="cover"
                      fallback={
                        <Avatar
                          size="xl"
                          name={influencer.name}
                          bg={getColor("primary")}
                          color="white"
                        />
                      }
                    />
                    <VStack spacing={2} textAlign="center">
                      <HStack spacing={2}>
                        <Text fontWeight="bold" fontSize="md" color={getColor("primary")}>
                          {influencer.name}
                        </Text>
                        <Icon as={HiShieldCheck} color={getColor("status.success")} />
                      </HStack>
                      <HStack spacing={1} fontSize="sm" color={getColor("text.muted")}>
                        <Icon as={HiLocationMarker} />
                        <Text>{influencer.country}</Text>
                      </HStack>
                    </VStack>
                    
                    <Text 
                      fontSize="sm" 
                      color={getColor("text.secondary")} 
                      textAlign="center"
                      noOfLines={2}
                      lineHeight="1.4"
                    >
                      {influencer.about}
                    </Text>
                    
                    <Wrap justify="center" spacing={1}>
                      {influencer.tags.slice(0, 2).map((tag) => (
                        <Tag 
                          key={tag} 
                          size="sm" 
                          bg={getColor("background.overlay")} 
                          color={getColor("text.primary")}
                          fontSize="xs"
                        >
                          {tag}
                        </Tag>
                      ))}
                    </Wrap>
                    
                    <VStack spacing={2} w="full">
                      <ChakraLink href={influencer.channelLink} target="_blank" w="full">
                        <Button 
                          size="sm"
                          variant="outline"
                          borderColor={getColor("border.medium")}
                          color={getColor("primary")}
                          w="full"
                          _hover={{ bg: getColor("background.overlay") }}
                        >
                          View Channel
                        </Button>
                      </ChakraLink>
                      {influencer.practitionerBookingUrl && (
                        <Button 
                          as={RouterLink}
                          to={influencer.practitionerBookingUrl}
                          size="sm"
                          bg={getColor("components.button.primaryBg")} 
                          color="white"
                          w="full"
                          _hover={{ bg: getColor("components.button.primaryHover") }}
                        >
                          Book Session
                        </Button>
                      )}
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* What, Why and Who Section */}
      <Box bg={getColor("background.card")}>
        <Container maxW="container.xl" py={{ base: "12", md: "16" }}>
          <Stack spacing={{ base: "8", md: "10" }} align="center">
            <Heading size={{ base: "lg", md: "xl" }} color={getColor("primary")} textAlign="center">
              What, Why and Who
            </Heading>
            <VStack spacing={6} maxW="5xl">
              <Text fontSize={{ base: "lg", md: "xl" }} color={getColor("text.secondary")} lineHeight="1.7">
                Multipolar Peace is first and foremost a resource site. It brings together online channels & voices that are part of a global dialogue towards multipolarity, neutrality and peace & prosperity.
              </Text>
              <Text fontSize={{ base: "lg", md: "xl" }} color={getColor("text.secondary")} lineHeight="1.7">
                <Text as="span" fontWeight="bold" color={getColor("primary")}>Who:</Text> It is a team effort, kicked off by a group of concerned global citizens. They are: Warwick Powell, Lena Petrova, Pascal Lottaz, Jeff Rich, Arnaud Bertrand, David Oualaalou, Einar Tangen, Digby Wren, Anna Rosario Malingdog-Uy, John Pang, SL Kanthan, Jerry Grey, Kathleen Tyson, Danny Haiphong, Thomas Fazi, Hussein Askary with support and encouragement from a bunch of others.
              </Text>
              <Text fontSize={{ base: "lg", md: "xl" }} color={getColor("text.secondary")} lineHeight="1.7">
                <Text as="span" fontWeight="bold" color={getColor("primary")}>Why:</Text> We came about in response to rising concerns of global instability and increased risk of warfare, particularly on the back of NATO's global expansion ambitions.
              </Text>
              <Text fontSize={{ base: "lg", md: "xl" }} color={getColor("text.secondary")} lineHeight="1.7">
                As a network Multipolar Peace is a team effort. At the same time, each platform's voice is unique. That's our own version of multipolarity in action.
              </Text>
              <Text fontSize={{ base: "lg", md: "xl" }} color={getColor("text.secondary")} lineHeight="1.7">
                We hope that by amplifying diverse voices, the message can be heard that Multipolar Peace is possible.
              </Text>
            </VStack>
          </Stack>
        </Container>
      </Box>

      {/* Book Call Section */}
      <Box bg={getColor("background.light")}>
        <Container maxW="container.xl" py={{ base: "12", md: "16" }}>
          <Stack spacing={{ base: "8", md: "10" }} align="center" textAlign="center">
            <Heading size={{ base: "lg", md: "xl" }} color={getColor("primary")}>
              Book a Call with Our Leaders
            </Heading>
            <Text fontSize={{ base: "lg", md: "xl" }} color={getColor("text.secondary")} maxW="4xl" lineHeight="1.7">
              Ready to engage in meaningful dialogue? Connect directly with Warwick Powell and other thought leaders above. Schedule a one-on-one session to discuss geopolitics, multipolarity, and peace building.
            </Text>
            <Button 
              as={RouterLink}
              to="/sessions/practitioner-calendar/684530057bb2388bfb7e2425"
              bg={getColor("components.button.primaryBg")} 
              color="white"
              size="lg"
              px={8}
              py={6}
              fontSize="lg"
              _hover={{ bg: getColor("components.button.primaryHover") }}
            >
              Book Session with Warwick Powell
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Secure Membership Section */}
      <Box bg={getColor("background.main")}>
        <Container maxW="container.xl" py={{ base: "12", md: "16" }}>
          <Stack spacing={{ base: "8", md: "10" }} align="center" textAlign="center">
            <Heading size={{ base: "lg", md: "xl" }} color={getColor("primary")}>
              Secure Your Membership
            </Heading>
            <Text fontSize={{ base: "lg", md: "xl" }} color={getColor("text.secondary")} maxW="4xl" lineHeight="1.7">
              Join our community of peace advocates and gain access to exclusive content, resources, and direct engagement with thought leaders shaping the multipolar world.
            </Text>
            <SecureMembershipButton size="lg" />
          </Stack>
        </Container>
      </Box>

      {/* Core Principles Grid */}
      <Box as="section" bg={getColor("background.light")}>
        <Container maxW="container.xl" py={{ base: "12", md: "16" }}>
          <VStack spacing={8} mb={12}>
            <Heading size={{ base: "lg", md: "xl" }} color={getColor("primary")} textAlign="center">
              Our Core Principles
            </Heading>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            <Box textAlign="center" p={8} bg={getColor("background.card")} borderRadius="xl" boxShadow="md">
              <Heading size="md" color={getColor("primary")} mb={4}>
                Multipolarity
              </Heading>
              <Text color={getColor("text.secondary")} fontSize="md" lineHeight="1.6">
                Multipolarity recognises and respects diversity of civilisations, building peace through respectful dialogue and collaboration.
              </Text>
            </Box>
            <Box textAlign="center" p={8} bg={getColor("background.card")} borderRadius="xl" boxShadow="md">
              <Heading size="md" color={getColor("primary")} mb={4}>
                Neutrality
              </Heading>
              <Text color={getColor("text.secondary")} fontSize="md" lineHeight="1.6">
                Neutrality enables engagement based on mutual respect and benefit, working together without being defined against others.
              </Text>
            </Box>
            <Box textAlign="center" p={8} bg={getColor("background.card")} borderRadius="xl" boxShadow="md">
              <Heading size="md" color={getColor("primary")} mb={4}>
                No NATO Expansion
              </Heading>
              <Text color={getColor("text.secondary")} fontSize="md" lineHeight="1.6">
                NATO expansion has been a main cause of conflict and bloodshed. Multipolarity does not need such alliances.
              </Text>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      <FooterWithFourColumns />

      <LoginWithSmsModal 
        isOpen={isOpen} 
        onClose={onClose}
        onLoginSuccess={() => onClose()}
      />
    </>
  );
};

export default HomePage;